/**
 * Curated locations for Top 10 predictions
 * Includes major cities, national parks, and world-famous sunset/sunrise spots
 */

export interface CuratedLocation {
  name: string;
  latitude: number;
  longitude: number;
  country: string;
  description?: string;
}

/**
 * Top USA locations known for spectacular sunrises and sunsets
 */
export const USA_LOCATIONS: CuratedLocation[] = [
  // Major Cities
  { name: "New York, NY", latitude: 40.7128, longitude: -74.0060, country: "USA", description: "Manhattan skyline" },
  { name: "Los Angeles, CA", latitude: 34.0522, longitude: -118.2437, country: "USA", description: "Pacific Coast sunsets" },
  { name: "San Francisco, CA", latitude: 37.7749, longitude: -122.4194, country: "USA", description: "Golden Gate Bridge" },
  { name: "Miami, FL", latitude: 25.7617, longitude: -80.1918, country: "USA", description: "South Beach" },
  { name: "Seattle, WA", latitude: 47.6062, longitude: -122.3321, country: "USA", description: "Puget Sound" },
  { name: "Chicago, IL", latitude: 41.8781, longitude: -87.6298, country: "USA", description: "Lake Michigan" },
  { name: "Boston, MA", latitude: 42.3601, longitude: -71.0589, country: "USA", description: "Harbor views" },
  { name: "Denver, CO", latitude: 39.7392, longitude: -104.9903, country: "USA", description: "Rocky Mountain backdrop" },
  { name: "Austin, TX", latitude: 30.2672, longitude: -97.7431, country: "USA", description: "Hill Country" },
  { name: "Portland, OR", latitude: 45.5152, longitude: -122.6784, country: "USA", description: "Mount Hood views" },
  
  // National Parks & Natural Wonders
  { name: "Grand Canyon, AZ", latitude: 36.1069, longitude: -112.1129, country: "USA", description: "South Rim" },
  { name: "Yosemite, CA", latitude: 37.8651, longitude: -119.5383, country: "USA", description: "Half Dome & El Capitan" },
  { name: "Yellowstone, WY", latitude: 44.4280, longitude: -110.5885, country: "USA", description: "Geothermal features" },
  { name: "Zion National Park, UT", latitude: 37.2982, longitude: -113.0263, country: "USA", description: "Red rock canyons" },
  { name: "Acadia National Park, ME", latitude: 44.3386, longitude: -68.2733, country: "USA", description: "Cadillac Mountain" },
  { name: "Monument Valley, UT", latitude: 36.9980, longitude: -110.0985, country: "USA", description: "Iconic buttes" },
  { name: "Sedona, AZ", latitude: 34.8697, longitude: -111.7610, country: "USA", description: "Red rock formations" },
  { name: "Key West, FL", latitude: 24.5551, longitude: -81.7800, country: "USA", description: "Southernmost point" },
  { name: "Big Sur, CA", latitude: 36.2704, longitude: -121.8081, country: "USA", description: "Pacific coastline" },
  { name: "Maui, HI", latitude: 20.7984, longitude: -156.3319, country: "USA", description: "Haleakalā sunrise" },
];

/**
 * Top Global locations known for spectacular sunrises and sunsets
 */
export const GLOBAL_LOCATIONS: CuratedLocation[] = [
  // Europe
  { name: "Santorini, Greece", latitude: 36.3932, longitude: 25.4615, country: "Greece", description: "Oia sunset" },
  { name: "Paris, France", latitude: 48.8566, longitude: 2.3522, country: "France", description: "Eiffel Tower" },
  { name: "London, UK", latitude: 51.5074, longitude: -0.1278, country: "UK", description: "Thames River" },
  { name: "Barcelona, Spain", latitude: 41.3851, longitude: 2.1734, country: "Spain", description: "Mediterranean coast" },
  { name: "Venice, Italy", latitude: 45.4408, longitude: 12.3155, country: "Italy", description: "Grand Canal" },
  { name: "Reykjavik, Iceland", latitude: 64.1466, longitude: -21.9426, country: "Iceland", description: "Midnight sun" },
  
  // Asia
  { name: "Tokyo, Japan", latitude: 35.6762, longitude: 139.6503, country: "Japan", description: "Mount Fuji views" },
  { name: "Bali, Indonesia", latitude: -8.3405, longitude: 115.0920, country: "Indonesia", description: "Tanah Lot Temple" },
  { name: "Dubai, UAE", latitude: 25.2048, longitude: 55.2708, country: "UAE", description: "Burj Khalifa" },
  { name: "Bangkok, Thailand", latitude: 13.7563, longitude: 100.5018, country: "Thailand", description: "Chao Phraya River" },
  { name: "Singapore", latitude: 1.3521, longitude: 103.8198, country: "Singapore", description: "Marina Bay" },
  { name: "Maldives", latitude: 3.2028, longitude: 73.2207, country: "Maldives", description: "Overwater bungalows" },
  
  // Africa & Middle East
  { name: "Cape Town, South Africa", latitude: -33.9249, longitude: 18.4241, country: "South Africa", description: "Table Mountain" },
  { name: "Marrakech, Morocco", latitude: 31.6295, longitude: -7.9811, country: "Morocco", description: "Atlas Mountains" },
  { name: "Serengeti, Tanzania", latitude: -2.3333, longitude: 34.8333, country: "Tanzania", description: "Safari plains" },
  { name: "Jerusalem, Israel", latitude: 31.7683, longitude: 35.2137, country: "Israel", description: "Old City" },
  
  // Oceania
  { name: "Sydney, Australia", latitude: -33.8688, longitude: 151.2093, country: "Australia", description: "Opera House & Harbour" },
  { name: "Queenstown, New Zealand", latitude: -45.0312, longitude: 168.6626, country: "New Zealand", description: "Lake Wakatipu" },
  { name: "Fiji", latitude: -17.7134, longitude: 178.0650, country: "Fiji", description: "Tropical islands" },
  
  // South America
  { name: "Rio de Janeiro, Brazil", latitude: -22.9068, longitude: -43.1729, country: "Brazil", description: "Christ the Redeemer" },
  { name: "Machu Picchu, Peru", latitude: -13.1631, longitude: -72.5450, country: "Peru", description: "Ancient ruins" },
  { name: "Patagonia, Argentina", latitude: -41.8102, longitude: -68.9063, country: "Argentina", description: "Mountain peaks" },
];

/**
 * Get all curated locations for a region
 */
export function getCuratedLocations(region: "usa" | "global"): CuratedLocation[] {
  return region === "usa" ? USA_LOCATIONS : GLOBAL_LOCATIONS;
}
